import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trophy, Loader2, Moon, Sun, ArrowLeft, MailCheck } from "lucide-react";

export default function Auth() {
  const { login, register } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetLink(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setSent(true);
      if (data.reset_link) setResetLink(data.reset_link);
      toast.success("If that email exists, a reset link is ready.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const goForgot = () => {
    setMode("forgot");
    setSent(false);
    setResetLink(null);
  };
  const backToLogin = () => {
    setMode("login");
    setSent(false);
    setResetLink(null);
  };

  return (
    <div className="min-h-screen bg-background relative flex items-center justify-center px-4 overflow-hidden">
      <img
        src="https://images.pexels.com/photos/28827841/pexels-photo-28827841.jpeg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-20 blur-2xl scale-110"
      />
      <div className="absolute inset-0 bg-background/60" />

      <Button
        variant="ghost"
        size="icon"
        onClick={toggle}
        data-testid="theme-toggle"
        className="absolute top-6 right-6 rounded-full z-10"
      >
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md glass rounded-3xl p-8 sm:p-10"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/30 mb-4">
            <Trophy className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">PitchLedger</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track every soccer bet. Spot what wins.
          </p>
        </div>

        {mode === "forgot" ? (
          <div data-testid="forgot-view">
            <button
              type="button"
              onClick={backToLogin}
              data-testid="forgot-back"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
            >
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-sent">
                <MailCheck className="h-12 w-12 mx-auto text-[#34C759]" />
                <p className="text-sm text-muted-foreground">
                  If an account exists for <b>{form.email}</b>, a password reset link has been sent.
                  The link expires in 1 hour.
                </p>
                {resetLink && (
                  <div className="rounded-2xl bg-secondary/60 p-4 text-left space-y-2" data-testid="forgot-dev-link">
                    <p className="text-xs text-muted-foreground">
                      Email delivery isn't set up yet, so here's your reset link:
                    </p>
                    <a
                      href={resetLink}
                      data-testid="forgot-reset-link"
                      className="text-sm font-semibold text-primary break-all underline underline-offset-2"
                    >
                      Reset your password →
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4">
                <p className="text-sm text-muted-foreground -mt-1">
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    data-testid="forgot-email-input"
                    value={form.email}
                    onChange={set("email")}
                    required
                    placeholder="you@example.com"
                    className="rounded-xl h-11"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit"
                  className="w-full h-11 rounded-full font-semibold text-base"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </Button>
              </form>
            )}
          </div>
        ) : (
          <>
            <div className="flex gap-1 bg-secondary/70 rounded-full p-1 mb-6">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  data-testid={`tab-${m}`}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${
                    mode === m ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {m === "login" ? "Sign In" : "Sign Up"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={form.name}
                    onChange={set("name")}
                    required
                    placeholder="Your name"
                    className="rounded-xl h-11"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="you@example.com"
                  className="rounded-xl h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  value={form.password}
                  onChange={set("password")}
                  required
                  placeholder="••••••••"
                  className="rounded-xl h-11"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                className="w-full h-11 rounded-full font-semibold text-base"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>

            {mode === "login" && (
              <button
                type="button"
                onClick={goForgot}
                data-testid="forgot-password-link"
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors mt-4"
              >
                Forgot password?
              </button>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
